import { all, spawn, put, select, call } from 'redux-saga/effects';
import { has, keys, map } from 'ramda';
import { isEmpty, get } from 'lodash';
import { normalize } from 'normalizr';

import * as schema from '../schema';
import * as actions from '../actions/chatActions';
import * as types from '../constants/ActionTypes';
import * as AttachmentUtils from '../helpers/AttachmentUtils';
import { getMessagesSaga, hideShowChatSaga } from './sendSagas';
import { getChatIdByPathname, isDirectMessage } from '../helpers/ChatUtils';

export function* messageReceivedSaga(message) {
  const normalized = normalize(message, schema.messages);
  const { messages, attachments } = normalized.entities;
  const messageId = keys(messages)[0];

  const { chatId, externalId } = messages[messageId];

  const { identity: { id: identityId }, chats, router: { location: { pathname } }, messages: storeMessages, messagesPaging } = yield select(state => state);

  // We already have the message pushed locally, no need to replace it.
  if (Object.keys(storeMessages).includes(externalId)) {
    const attachmentIds = !isEmpty(attachments) ? Object.keys(attachments) : [];
    const localAttachmentIds = get(storeMessages[externalId], 'attachments', []);

    yield put({ type: types.UPDATE_LOCAL_MESSAGE, payload: { id: externalId, _id: parseInt(messageId, 10) } });

    if (!isEmpty(localAttachmentIds) && !isEmpty(attachments)) {
      // NOTE: If we ever allow multiple attachments, we would need an external id on each attachment as well
      //  so we know which attachment UUID maps to which attachment id in the database
      yield put({ type: types.UPDATE_LOCAL_ATTACHMENT, payload: { id: localAttachmentIds[0], _id: parseInt(attachmentIds[0], 10) } });
    }

    return;
  }

  if (!has(chatId, chats)) {
    yield put(actions.getChats([chatId]));
  } else {
    // If chat is DM and is hidden, show it
    const chat = chats[chatId];
    if (isDirectMessage(chat) && chat.hidden) {
      yield spawn(hideShowChatSaga, actions.showChat([chatId]));
    }

    const pathChatId = getChatIdByPathname(pathname);
    // 1. SenderId of null is a system message
    // 2. Don't count message as unread if you sent it
    const { senderId } = message.payload;
    if (senderId && identityId !== senderId && pathChatId !== chatId) {
      yield put(actions.incrementUnreadCount(chatId));
    }
  }

  // Fetch all images in each message for real-time inline rendering
  const images = keys(attachments).filter(id => AttachmentUtils.isNonSvgImage(attachments[id]));
  yield all(map(id => put(actions.downloadAttachment(id, false)), images));

  // Fetch SVGs using the websocket API so we can get the image in base64 so we can in-line render it.  I do not
  // have a good explanation why SVGs need to be handled differently when rendering other than that they are of xml
  // format - unlike other images
  const svgs = keys(attachments).filter(id => AttachmentUtils.isSvgImage(attachments[id]));
  yield all(map(id => put(actions.getAttachment(id)), svgs));

  // Fetch all video preview images for inline rendering of a playable video attachment
  const videoImages = keys(attachments).filter(id => AttachmentUtils.isVideo(attachments[id]));
  yield all(map(id => put(actions.downloadVideoImageAttachment(id)), videoImages));

  /*
   * If we receive a message for a chat we have not loaded yet, disregard it and call get Messages
   * which will in turn, grab the new message received as well
   */
  if (messagesPaging[chatId]) {
    yield put({ type: types.MESSAGE_RECEIVED_SUCCESS, payload: { chatId, messages, attachments } });
  } else {
    yield call(getMessagesSaga, actions.getMessages(chatId));
  }
}
